import { datadogRum } from "@datadog/browser-rum";
import { isDevEnvironment } from "../../utils/general";

/**
 * The running version, as the console shows on load and Datadog RUM records.
 *
 * - Dev and prod deploys: VITE_APP_VERSION, stamped by .github/workflows/deploy.yaml as
 *   YYYY.M.D-g<sha8> of the deployed commit (e.g. "2026.10.22-gb23a7dce").
 * - Local dev server: the Git SHA (package.json's version means nothing locally).
 *
 * @returns Version string for Datadog RUM tracking
 */
export const getDatadogVersion = (): string | undefined => {
  if (import.meta.env.VITE_APP_ENVIRONMENT === "local") {
    return import.meta.env.VITE_APP_GIT_SHA;
  }
  return import.meta.env.VITE_APP_VERSION || import.meta.env.VITE_APP_GIT_SHA;
};

/**
 * Configuration options for Datadog RUM initialization
 */
export interface DatadogConfig {
  /** Datadog RUM application ID */
  applicationId: string;
  /** Datadog client token (public, safe to expose in frontend) */
  clientToken: string;
  /** Service name (e.g., 'champions-frontend', 'learn-frontend') */
  service: string;
  /** Version/build identifier (e.g., git SHA) for tracking deployments */
  version?: string;
  /** Privacy level for sensitive data masking */
  defaultPrivacyLevel?: "mask-user-input" | "mask" | "allow";
  /** Percentage of traces to sample for APM (0-100) */
  traceSampleRate?: number;
  /** Regex patterns for backend APIs to enable distributed tracing */
  allowedTracingOrigins?: (string | RegExp)[];
}

/**
 * Initialize Datadog RUM (Real User Monitoring)
 *
 * Tracks frontend performance, errors, and user interactions.
 * Based on knowledge-assessments (ka) configuration - the gold standard.
 *
 * **Local Development:** Automatically disabled when running locally (pnpm start / pnpm run atlas)
 * to prevent telemetry pollution from developer machines. Only initializes in CI/CD builds where
 * VITE_APP_ENVIRONMENT is explicitly set to "development" or "production".
 *
 * Features enabled:
 * - Session replay recording
 * - Performance monitoring (Core Web Vitals, resources, long tasks)
 * - User interaction tracking (clicks, inputs)
 * - Frustration signals (rage clicks, error clicks, dead clicks)
 * - Distributed tracing to backend APIs
 * - Cross-subdomain session tracking
 *
 * @param config - Datadog configuration options
 *
 * @example
 * ```typescript
 * initDatadog({
 *   applicationId: "abc-123",
 *   clientToken: "pub456",
 *   service: "my-frontend",
 *   version: import.meta.env.VITE_APP_GIT_SHA,
 * });
 * ```
 */
export const initDatadog = ({
  applicationId,
  clientToken,
  service,
  version,
  defaultPrivacyLevel = "mask-user-input",
  traceSampleRate = 10,
  allowedTracingOrigins = [
    /https:\/\/.*\.securityjourney\.(?:com|dev)/,
    /https:\/\/.*\.hackedu\.(?:com|dev)/,
  ],
}: DatadogConfig) => {
  // Disable Datadog in local development mode
  // VITE_APP_ENVIRONMENT is only set in CI/CD builds (via set_dev_env.sh / set_prod_env.sh)
  // When running locally (pnpm start / pnpm run atlas), it's undefined
  const environment = import.meta.env.VITE_APP_ENVIRONMENT;
  if (!environment || environment === "local") {
    console.info("ℹ️ Datadog RUM disabled (local development mode)");
    return;
  }

  datadogRum.init({
    applicationId,
    clientToken,
    // `site` refers to the Datadog site parameter of your organization
    // see https://docs.datadoghq.com/getting_started/site/
    site: "datadoghq.com",
    service,
    env: isDevEnvironment() ? "development" : "production",
    // Specify a version number to identify the deployed version of your application in Datadog
    version,
    sessionSampleRate: 10, // Track 100% of user sessions
    sessionReplaySampleRate: 10, // Record 10% of sessions for replay
    defaultPrivacyLevel, // Default: "mask-user-input"

    // ✅ Advanced tracking features
    traceSampleRate, // APM distributed tracing (10% of requests)
    trackResources: true, // Track XHR, Fetch, CSS, JS, images, fonts
    trackLongTasks: true, // Track tasks >50ms that block the main thread
    trackUserInteractions: true, // Track user clicks, inputs, navigation (includes frustration signals)
    trackViewsManually: false, // Auto-track page views

    // ✅ Cross-domain and security settings
    trackSessionAcrossSubdomains: true, // Track across *.securityjourney.com
    useSecureSessionCookie: true, // Set Secure flag on cookies

    // ✅ Distributed tracing to backend APIs
    allowedTracingUrls: allowedTracingOrigins, // Inject trace headers for APM correlation
  });

  // Start session replay recording for visual debugging
  datadogRum.startSessionReplayRecording();
};
